import { useEffect, useState, useRef, useCallback } from 'react';
import { getContentById } from '../utils/firebaseService';
import { detectContentType } from '../utils/contentUtils';

export default function useContent(contentId, user) {
	const [currentContent, setCurrentContent] = useState(null);
	const [contentType, setContentType] = useState('text');
	const [isLoading, setIsLoading] = useState(true);

	const retryCountRef = useRef(0);
	const isMountedRef = useRef(true);

	const fetchContent = useCallback(async () => {
		if (!contentId) {
			if (isMountedRef.current) setIsLoading(false);
			return;
		}
		if (!user) return;

		if (isMountedRef.current) setIsLoading(true);
		try {
			const content = await getContentById(contentId);
			if (content && isMountedRef.current) {
				setCurrentContent(content);
				setContentType(detectContentType(content));
			}
		} finally {
			if (isMountedRef.current) setIsLoading(false);
		}
	}, [contentId, user]);

	useEffect(() => {
		isMountedRef.current = true;

		const maxRetries = 3;
		const retryDelay = 1000;

		const tryFetch = async () => {
			try {
				await fetchContent();
			} catch (err) {
				if (retryCountRef.current < maxRetries && isMountedRef.current) {
					retryCountRef.current += 1;
					setTimeout(tryFetch, retryDelay);
				} else {
					if (isMountedRef.current) setIsLoading(false);
				}
			}
		};

		if (contentId && user) tryFetch();
		else setIsLoading(false);

		return () => { isMountedRef.current = false; };
	}, [contentId, user, fetchContent]);

	return { currentContent, contentType, setContentType, isLoading, refetch: fetchContent };
}
